



























































































import { computed, defineComponent, getCurrentInstance, ref } from "@vue/composition-api";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities, checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { AuthUserAuthorities, LoginSignUpModalData, BaseMenuItem } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseIcon from "@/components/BaseIcon";
import BaseMenu from "@/components/BaseMenu";
import Share from "@/components/Share";
import UserFollowSubscribeModal from "@/components/UserFollowSubscribeModal";
import ProfileHeader from "../ProfileHeader";
import ProfileStatistic from "../ProfileStatistic";
import QuestionaryInfoPrinter from "../QuestionaryInfoPrinter";

export default defineComponent({
  name: "ProfileInfoView",
  components: {
    QuestionaryInfoPrinter,
    ProfileHeader,
    BaseButton,
    BaseMenu,
    BaseIcon,
    ProfileStatistic,
    Share,
    UserFollowSubscribeModal,
  },
  props: {
    user: { type: Object as () => UserPublicData, required: true },
    questionaryInfo: { type: Object as () => Record<string, any>, default: () => ({}) },
    isFollowing: { type: Boolean, required: true },
    isSubscribed: { type: Boolean, required: true },
    isBlocked: { type: Boolean, required: false },
    isAuthor: { type: Boolean, required: true },
    hasSubscriptionLevels: { type: Boolean, required: true },
    hasContentCount: { type: Boolean, required: true },
    verifiedIconSize: { type: [Number, String], required: false, default: 16 },
  },
  setup(props, { emit }) {
    const showFollow = ref(false);

    const screenName = computed<string>(() => props.user.screenName);
    const username = computed<string>(() => props.user.username);
    const verified = computed<boolean>(() => props.user.verified);

    const showOnlineStatus = computed<boolean>(() => config.featureUserOnlineStatus);

    const { authorities, authUser, updateUser } = useAuthUser();
    const canEditInfo = computed<boolean>(() => {
      const requiredAuthorities: AuthUserAuthorities = props.isAuthor
        ? ["updateMyQuestionaryInfo", "updateMyPersonalInfo", "updateMyFile"]
        : ["updateAnyQuestionaryInfo", "updateAnyPersonalInfo", "updateAnyFile"];
      return checkAuthorities(authorities.value, requiredAuthorities);
    });

    const isDisabled = computed<boolean>(() => props.user.disabled ?? true);
    const showSubscribeButton = computed<boolean>(
      () =>
        !isDisabled.value && !props.isBlocked && !props.isAuthor && props.hasSubscriptionLevels && props.hasContentCount
    );
    const showFollowButton = computed(() => !isDisabled.value && !props.isBlocked && !props.isAuthor);
    const canChangeVisibility = computed(() => checkRole(authUser.value?.role || "", "creator") && props.isAuthor);
    const lastOnline = computed(() => {
      if (props.user.onlineStatus?.isOnline && !props.user.onlineStatus?.invisible) return $t("Online");
      if (props.user.onlineStatus?.isOnline && props.user.onlineStatus?.invisible && canChangeVisibility.value)
        return $t("Invisible");
    });

    const activeUserMode = ref(lastOnline.value);

    const userModes = computed<BaseMenuItem[]>(() => [
      {
        text: $t("Online"),
        name: "Online",
        class: activeUserMode.value === "Online" ? "fw-6oo" : "",
      },
      {
        text: $t("Invisible"),
        name: "Invisible",
        class: activeUserMode.value === "Invisible" ? "fw-6oo" : "",
      },
    ]);

    const onSignUpSuccess = async () => {
      await providers.FollowingsProvider.toggle({ id: props.user.id });
      emit("onFollowUpdate", true);
    };
    const loginSignUpModalData: LoginSignUpModalData = { onSignUpSuccess };
    const onFollowingClick = useAuthorisedOnly(async () => {
      if (!props.isFollowing) {
        await providers.FollowingsProvider.toggle({ id: props.user.id });
        emit("onFollowUpdate", true);
      }
      showFollow.value = true;
    }, loginSignUpModalData);
    const onSubscribeClick = useAuthorisedOnly(() => (showFollow.value = true));

    const vm = getCurrentInstance();
    const sendMessage = useAuthorisedOnly(async () => {
      await vm?.$router.push({ name: "messages", params: { userId: props.user.id } });
    }, loginSignUpModalData);

    const onSelectMode = (value: string) => {
      activeUserMode.value = value;
      const invisible = value === "Invisible";
      providers.OnlineStatusProvider.updateVisibility({ data: { invisible } });
      authUser.value && updateUser(authUser.value);
      emit("updateUser");
    };

    return {
      username,
      verified,
      showFollow,
      isDisabled,
      screenName,
      canEditInfo,
      showOnlineStatus,
      showSubscribeButton,
      showFollowButton,
      sendMessage,
      onFollowingClick,
      onSubscribeClick,
      userModes,
      activeUserMode,
      canChangeVisibility,
      lastOnline,
      onSelectMode,
    };
  },
});
